// ABOUTME: Modal for generating Shopify OAuth install links and editing existing connectors.
// ABOUTME: Create mode supports pre-registering a connector or generating an install link; edit mode updates resources and schedule.

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Link, Copy, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { shopifyConnectorApi } from '@/services/api/adminClient';
import type {
  ShopifyConnector,
  ShopifyConnectorUpdateRequest,
} from '@/types/admin';

interface ShopifyConnectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  connector?: ShopifyConnector;
  onSaved: (connector: ShopifyConnector) => void;
}

const AVAILABLE_RESOURCES = [
  { value: 'products', label: 'Products' },
  { value: 'orders', label: 'Orders' },
  { value: 'customers', label: 'Customers' },
];

const FREQUENCY_OPTIONS = [
  { value: 60, label: 'Every hour' },
  { value: 360, label: 'Every 6 hours' },
  { value: 720, label: 'Every 12 hours' },
  { value: 1440, label: 'Every 24 hours' },
];

export function ShopifyConnectorModal({
  isOpen,
  onClose,
  workspaceId,
  connector,
  onSaved,
}: ShopifyConnectorModalProps) {
  const isEditing = Boolean(connector);

  // Create-mode fields
  const [shopUrl, setShopUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  // Shared fields
  const [selectedResources, setSelectedResources] = useState<string[]>(['products', 'orders', 'customers']);
  const [frequencyMinutes, setFrequencyMinutes] = useState(1440);
  const [startDate, setStartDate] = useState('');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create mode selector — default to 'register' (production flow)
  const [createMode, setCreateMode] = useState<'link' | 'register'>('register');

  // Install link result (create mode, link flow only)
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [installExpiresAt, setInstallExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Register success (create mode, register flow only)
  const [registerSuccess, setRegisterSuccess] = useState(false);

  // Initialize from connector when editing
  useEffect(() => {
    if (connector) {
      setSelectedResources(connector.resources);
      setFrequencyMinutes(connector.schedule_frequency_minutes);
      setStartDate(connector.initial_sync_start_date || '');
    } else {
      resetForm();
    }
    setError(null);
  }, [connector, isOpen]);

  const resetForm = () => {
    setShopUrl('');
    setClientId('');
    setClientSecret('');
    setSelectedResources(['products', 'orders', 'customers']);
    setFrequencyMinutes(1440);
    setStartDate('');
    setInstallUrl(null);
    setInstallExpiresAt(null);
    setCopied(false);
    setRegisterSuccess(false);
    setError(null);
  };

  const toggleResource = (value: string) => {
    setSelectedResources((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
  };

  const handleGenerateLink = async () => {
    if (!shopUrl.trim() || !clientId.trim() || !clientSecret.trim()) {
      setError('Shop URL, Client ID, and Client Secret are required');
      return;
    }
    if (selectedResources.length === 0) {
      setError('Select at least one resource');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const result = await shopifyConnectorApi.generateInstallLink(workspaceId, {
        shop_url: shopUrl.trim(),
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        resources: selectedResources,
        schedule_frequency_minutes: frequencyMinutes,
        initial_sync_start_date: startDate || undefined,
      });
      setInstallUrl(result.install_url);
      setInstallExpiresAt(result.expires_at);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate install link');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegister = async () => {
    if (!shopUrl.trim() || !clientId.trim() || !clientSecret.trim()) {
      setError('Shop URL, Client ID, and Client Secret are required');
      return;
    }
    if (selectedResources.length === 0) {
      setError('Select at least one resource');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await shopifyConnectorApi.register(workspaceId, {
        shop_url: shopUrl.trim(),
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        resources: selectedResources,
        schedule_frequency_minutes: frequencyMinutes,
        initial_sync_start_date: startDate || undefined,
      });
      setRegisterSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register connector');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (selectedResources.length === 0) {
      setError('Select at least one resource');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updateRequest: ShopifyConnectorUpdateRequest = {
        resources: selectedResources,
        schedule_frequency_minutes: frequencyMinutes,
        initial_sync_start_date: startDate || undefined,
      };
      const saved = await shopifyConnectorApi.update(workspaceId, updateRequest);
      onSaved(saved);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connector');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!installUrl) return;
    try {
      await navigator.clipboard.writeText(installUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = installUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-lg bg-surface-0 border border-surface-200 shadow-modal rounded-sm p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-success-light flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-success" />
              </div>
              <h2 className="text-xl font-semibold text-surface-800">
                {isEditing ? 'Edit Shopify Connector' : 'Connect Shopify'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-surface-100 rounded-sm hover:scale-105 transition-colors"
            >
              <X className="w-5 h-5 text-surface-500" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-sm bg-error-light border border-error/30 text-error text-sm">
              {error}
            </div>
          )}

          {/* Install Link Result (create mode, link flow, after generation) */}
          {!isEditing && installUrl && (
            <div className="mb-6 space-y-4">
              <div className="p-4 rounded-sm bg-success-light border border-success/30">
                <div className="flex items-center gap-2 mb-2">
                  <Link className="w-4 h-4 text-success" />
                  <span className="text-sm font-medium text-success">Install link generated</span>
                </div>
                <p className="text-xs text-surface-600 mb-3">
                  Send this link to the customer. They will be redirected to Shopify to authorize access.
                  {installExpiresAt && (
                    <> This link expires at {new Date(installExpiresAt).toLocaleString()}.</>
                  )}
                </p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={installUrl}
                    className="flex-1 px-3 py-2 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-xs font-mono truncate"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyLink}
                    leftIcon={copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-surface-500">
                The connector will be created automatically once the customer completes the Shopify
                authorization. You can close this modal and check back later.
              </p>

              <Button variant="ghost" onClick={handleClose} className="w-full">
                Done
              </Button>
            </div>
          )}

          {/* Register Success (create mode, register flow, after registration) */}
          {!isEditing && registerSuccess && (
            <div className="mb-6 space-y-4">
              <div className="p-4 rounded-sm bg-success-light border border-success/30">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span className="text-sm font-medium text-success">Connector registered</span>
                </div>
                <p className="text-xs text-surface-600">
                  The connector is waiting for the merchant to install the Shopify app.
                  Send them the install link from the Shopify dev dashboard. The connector
                  will activate automatically once they complete the installation.
                </p>
              </div>
              <Button variant="ghost" onClick={handleClose} className="w-full">
                Done
              </Button>
            </div>
          )}

          {/* Form (hidden after install link is generated or register succeeds in create mode) */}
          {(isEditing || (!installUrl && !registerSuccess)) && (
            <>
              <div className="space-y-4 mb-6">
                {/* Create-only fields: mode selector + credentials */}
                {!isEditing && (
                  <>
                    {/* Setup method selector */}
                    <div>
                      <label className="block text-sm font-medium text-surface-600 mb-2">Setup method</label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setCreateMode('register')}
                          className={`flex-1 px-3 py-2 text-sm rounded-sm border transition-colors ${
                            createMode === 'register'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                          }`}
                        >
                          Register for Merchant Install
                        </button>
                        <button
                          type="button"
                          onClick={() => setCreateMode('link')}
                          className={`flex-1 px-3 py-2 text-sm rounded-sm border transition-colors ${
                            createMode === 'link'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                          }`}
                        >
                          Generate Install Link
                        </button>
                      </div>
                      <p className="mt-1.5 text-xs text-surface-500">
                        {createMode === 'register'
                          ? 'Pre-register the connector, then send the Shopify install link to the merchant.'
                          : 'Generate a one-time install link for dev/test stores.'}
                      </p>
                    </div>

                    <Input
                      label="Shop URL"
                      placeholder="your-store.myshopify.com"
                      value={shopUrl}
                      onChange={(e) => setShopUrl(e.target.value)}
                      helperText="The customer's Shopify store domain"
                    />

                    <Input
                      label="Client ID"
                      placeholder="Shopify app client ID"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      helperText="From the Shopify app dashboard (dev.shopify.com)"
                    />

                    <Input
                      label="Client Secret"
                      placeholder="Shopify app client secret"
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                    />
                  </>
                )}

                {/* Resources */}
                <div>
                  <label className="block text-sm font-medium text-surface-600 mb-2">
                    Resources to sync
                  </label>
                  <div className="space-y-2">
                    {AVAILABLE_RESOURCES.map(({ value, label }) => (
                      <label key={value} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedResources.includes(value)}
                          onChange={() => toggleResource(value)}
                          className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-surface-800">{label}</span>
                      </label>
                    ))}
                  </div>
                  {selectedResources.length === 0 && (
                    <p className="mt-1.5 text-xs text-error">At least one resource must be selected</p>
                  )}
                </div>

                {/* Sync frequency */}
                <div>
                  <label className="block text-sm font-medium text-surface-600 mb-1.5">
                    Sync frequency
                  </label>
                  <select
                    value={frequencyMinutes}
                    onChange={(e) => setFrequencyMinutes(Number(e.target.value))}
                    className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                  >
                    {FREQUENCY_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Initial sync start date */}
                <Input
                  label="Sync data from"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  helperText={
                    isEditing
                      ? 'Setting an earlier date will backfill older data on the next sync'
                      : 'Leave empty to sync the last 2 years of data'
                  }
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleClose} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={isEditing ? handleSaveEdit : (createMode === 'register' ? handleRegister : handleGenerateLink)}
                  isLoading={isSaving}
                  disabled={selectedResources.length === 0}
                  className="flex-1"
                  leftIcon={!isSaving ? (isEditing ? <ShoppingBag className="w-4 h-4" /> : <Link className="w-4 h-4" />) : undefined}
                >
                  {isEditing ? 'Save Changes' : (createMode === 'register' ? 'Register Connector' : 'Generate Install Link')}
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ShopifyConnectorModal;
